import {
  loadSVG,
  isPositionInPath,
  samplePathPoints,
  extractTranslate,
  segmentAngle,
  directionVectors,
  formatLapTime,
  delay,
  copyToClipboard,
  isInUncontrolledIframe,
} from "./utils.mjs";
import { initSparks, updateSparksPhysics, renderSparks } from "./sparks.mjs";
import {
  runPhysics,
  resetPhysicsTiming,
  getInterpolationAlpha,
} from "./physics-timing.mjs";
import {
  recordLocalGhost,
  saveLocalGhost,
  loadLocalGhost,
  clearLocalGhost,
  getLocalGhostSamples,
} from "./ghost.mjs";
import { Car } from "./car.mjs";
import {
  TRACK_NAMES,
  TRACK_DISPLAY_NAMES,
  TEAMS,
  GAME_VERSION,
} from "./constants.mjs";
import {
  displayLeaderboard,
  displayLoadingLeaderboard,
  loadLeaderboard,
  loadAllLeaderboards,
  clearLeaderboardCache,
  setSelectedEntryName,
  insertLeaderboardEntryAt,
  resetBestLapDialog,
  submitBestLapTime,
  getLapTimeRank,
  loadGhostBinary,
  loadedGhosts,
} from "./leaderboard.mjs";
import { fireConfetti } from "./confetti.mjs";
import { MusicPlayer } from "./music-player.mjs";
import { SoundPlayer } from "./sound-player.mjs";

// Import all track images using Parcel's url: prefix
import trackAbudhabiImg from "url:./assets/img/track-abudhabi.png";
import trackAlbertparkImg from "url:./assets/img/track-albertpark.png";
import trackAmericasImg from "url:./assets/img/track-americas.png";
import trackBahrainImg from "url:./assets/img/track-bahrain.png";
import trackBakuImg from "url:./assets/img/track-baku.png";
import trackBudapestImg from "url:./assets/img/track-budapest.png";
import trackCatalunyaImg from "url:./assets/img/track-catalunya.png";
import trackHockenheimImg from "url:./assets/img/track-hockenheim.png";
import trackInterlagosImg from "url:./assets/img/track-interlagos.png";
import trackMarinabayImg from "url:./assets/img/track-marinabay.png";
import trackMexicoImg from "url:./assets/img/track-mexico.png";
import trackMonacoImg from "url:./assets/img/track-monaco.png";
import trackMontrealImg from "url:./assets/img/track-montreal.png";
import trackMonzaImg from "url:./assets/img/track-monza.png";
import trackPaulricardImg from "url:./assets/img/track-paulricard.png";
import trackShanghaiImg from "url:./assets/img/track-shanghai.png";
import trackSilverstoneImg from "url:./assets/img/track-silverstone.png";
import trackSochiImg from "url:./assets/img/track-sochi.png";
import trackSpaImg from "url:./assets/img/track-spa.png";
import trackSpielbergImg from "url:./assets/img/track-spielberg.png";
import trackSuzukaImg from "url:./assets/img/track-suzuka.png";

// Import other game assets
import carSpritesImg from "url:./assets/img/car-sprites.png";
import checkeredStripImg from "url:./assets/img/checkered-strip.png";
import checkeredFlagImg from "url:./assets/img/checkered-flag.png";
import gameLogo from "url:./assets/img/game-logo-v2.png";
import startFinishImg from "url:./assets/img/start-finish.png";
import startLightsImg from "url:./assets/img/start-lights.png";
import startingGridImg from "url:./assets/img/starting-grid.png";
import spinnerImg from "url:./assets/img/spinner.png";

// Import SVG files
import trackAbudhabiSvg from "url:./assets/img/track-abudhabi.svg";
import trackAlbertparkSvg from "url:./assets/img/track-albertpark.svg";
import trackAmericasSvg from "url:./assets/img/track-americas.svg";
import trackBahrainSvg from "url:./assets/img/track-bahrain.svg";
import trackBakuSvg from "url:./assets/img/track-baku.svg";
import trackBudapestSvg from "url:./assets/img/track-budapest.svg";
import trackCatalunyaSvg from "url:./assets/img/track-catalunya.svg";
import trackHockenheimSvg from "url:./assets/img/track-hockenheim.svg";
import trackInterlagosSvg from "url:./assets/img/track-interlagos.svg";
import trackMarinabaySvg from "url:./assets/img/track-marinabay.svg";
import trackMexicoSvg from "url:./assets/img/track-mexico.svg";
import trackMonacoSvg from "url:./assets/img/track-monaco.svg";
import trackMontrealSvg from "url:./assets/img/track-montreal.svg";
import trackMonzaSvg from "url:./assets/img/track-monza.svg";
import trackPaulricardSvg from "url:./assets/img/track-paulricard.svg";
import trackShanghaiSvg from "url:./assets/img/track-shanghai.svg";
import trackSilverstoneSvg from "url:./assets/img/track-silverstone.svg";
import trackSochiSvg from "url:./assets/img/track-sochi.svg";
import trackSpaSvg from "url:./assets/img/track-spa.svg";
import trackSpielbergSvg from "url:./assets/img/track-spielberg.svg";
import trackSuzukaSvg from "url:./assets/img/track-suzuka.svg";

// Import Audio files
import engineLoopSound from "url:./assets/sounds/engine-loop.ogg";
import gameMusic from "url:./assets/sounds/game-music.ogg";
import titleMusic from "url:./assets/sounds/title-music.ogg";
import startLightSound from "url:./assets/sounds/start-light.ogg";
import buttonClickSound from "url:./assets/sounds/button-click.ogg";
import {
  classifyPerf,
  getStoredPerfTier,
  runPerfBenchmark,
  storePerfTier,
} from "./perf.mjs";

// Create mapping objects for dynamic access
const trackImages = {
  abudhabi: trackAbudhabiImg,
  albertpark: trackAlbertparkImg,
  americas: trackAmericasImg,
  bahrain: trackBahrainImg,
  baku: trackBakuImg,
  budapest: trackBudapestImg,
  catalunya: trackCatalunyaImg,
  hockenheim: trackHockenheimImg,
  interlagos: trackInterlagosImg,
  marinabay: trackMarinabayImg,
  mexico: trackMexicoImg,
  monaco: trackMonacoImg,
  montreal: trackMontrealImg,
  monza: trackMonzaImg,
  paulricard: trackPaulricardImg,
  shanghai: trackShanghaiImg,
  silverstone: trackSilverstoneImg,
  sochi: trackSochiImg,
  spa: trackSpaImg,
  spielberg: trackSpielbergImg,
  suzuka: trackSuzukaImg,
};

const trackSvgs = {
  abudhabi: trackAbudhabiSvg,
  albertpark: trackAlbertparkSvg,
  americas: trackAmericasSvg,
  bahrain: trackBahrainSvg,
  baku: trackBakuSvg,
  budapest: trackBudapestSvg,
  catalunya: trackCatalunyaSvg,
  hockenheim: trackHockenheimSvg,
  interlagos: trackInterlagosSvg,
  marinabay: trackMarinabaySvg,
  mexico: trackMexicoSvg,
  monaco: trackMonacoSvg,
  montreal: trackMontrealSvg,
  monza: trackMonzaSvg,
  paulricard: trackPaulricardSvg,
  shanghai: trackShanghaiSvg,
  silverstone: trackSilverstoneSvg,
  sochi: trackSochiSvg,
  spa: trackSpaSvg,
  spielberg: trackSpielbergSvg,
  suzuka: trackSuzukaSvg,
};

const TRACK_SCALE = 20;
const cameraZoom = 0.5;
const SKID_CANVAS_WIDTH = 1000;
const SKID_CANVAS_HEIGHT = 1000;
const SKID_INTERVAL_SQ = Math.pow(25, 2);
const VIEWPORT_WIDTH = 1600;
const VIEWPORT_HEIGHT = 900;

const assetsManifest = [];
let ASSETS = {};
let isDemo = true;
let isInit = false;
let isRacingLineVisible = false;
let selectedTrackName;
let currentTrackName = TRACK_NAMES[0];
let hvw = VIEWPORT_WIDTH * 0.5;
let hvh = VIEWPORT_HEIGHT * 0.5;
let startLine = 22;
let finishLine = 23;
let lapCount = 1;
let isRaceStarted = false;
let isRaceFinished = false;
let isPaused = false;
let now = Date.now();
let raceStartTime = now;
let lastPausedTime = -1;
let showSkidEffects = true;
let showSparkEffects = true;
let buttonClickBuffer;
let startLightBuffer;
let titleMusicBuffer;
let gameMusicBuffer;
let currentStartLightCount = -1;

// FPS tracking
let lastFpsTime = now;
let frameCount = 0;
let fps = 60;

const $startGameButton = document.getElementById("startGameButton");
const $game = document.getElementById("game");
const $cars = document.getElementById("cars");
const $track = document.getElementById("track");
const $randomTrackOption = document.getElementById("randomTrackOption");
const $samples = document.getElementById("samples");
const $startLine = document.getElementById("startLine");
const $finishLine = document.getElementById("finishLine");
const $gridContainer = document.getElementById("gridContainer");
const $lapLabel = document.getElementById("lapLabel");
const $speedLabel = document.getElementById("speedLabel");
const $trackLabel = document.getElementById("trackLabel");
const $lapTimeLabel = document.getElementById("lapTimeLabel");
const $skids = document.getElementById("skids");
const $sparks = document.getElementById("sparks");
const $startLights = document.getElementById("startLights");
const $pauseDialog = document.getElementById("pauseDialog");
const $pauseResumeRaceButton = document.getElementById("pauseResumeRaceButton");
const $pauseQuitRaceButton = document.getElementById("pauseQuitRaceButton");
const $pauseDialogCloseButton = document.getElementById(
  "pauseDialogCloseButton"
);
const $startRaceDialog = document.getElementById("startRaceDialog");
const $startRaceDialogCloseButton = document.getElementById(
  "startRaceDialogCloseButton"
);
const $startRaceTrackTitle = document.getElementById("startRaceTrackTitle");
const $startRaceTrackInfo = document.getElementById("startRaceTrackInfo");
const $startRaceLeaderboardSlider = document.getElementById("startRaceLeaderboardSlider");
const $startRaceLeaderboardWeekly = document.getElementById("startRaceLeaderboardWeekly");
const $startRaceLeaderboardMonthly = document.getElementById("startRaceLeaderboardMonthly");
const $startRaceLeaderboardAllTime = document.getElementById("startRaceLeaderboardAllTime");
const $startRaceLeaderboardTabs = document.querySelectorAll("#startRaceDialog .leaderboard-tab");
const $startRaceButton = document.getElementById("startRaceButton");
const $startRaceShareButton = document.getElementById("startRaceShareButton");
const $startRaceTeamOptions = document.getElementById("startRaceTeamOptions");
const $bestLapDialog = document.getElementById("bestLapDialog");
const $bestLapLeaderboard = document.getElementById("bestLapLeaderboard");
const $bestLapLeaderboardType = document.getElementById("bestLapLeaderboardType");
const $bestLapInputMessage = document.getElementById("bestLapInputMessage");
const $bestLapSubmitButton = document.getElementById("bestLapSubmitButton");
const $bestLapDialogCloseButton = document.getElementById(
  "bestLapDialogCloseButton"
);
const $bestLapShareButton = document.getElementById("bestLapShareButton");
const $bestLapInput = document.getElementById("bestLapInput");
const $copyFallbackDialog = document.getElementById("copyFallbackDialog");
const $copyFallbackInput = document.getElementById("copyFallbackInput");
const $copyFallbackCloseButton = document.getElementById(
  "copyFallbackCloseButton"
);
const $fpsCounter = document.getElementById("fpsCounter");
const $viz = document.getElementById("viz");
const $steeringViz = document.getElementById("steeringViz");
const $steeringAccViz = document.getElementById("steeringAccViz");
const $accelerationViz = document.getElementById("accelerationViz");
const $speedViz = document.getElementById("speedViz");
const $lapProgressViz = document.getElementById("lapProgressViz");
const $lapTimes = document.getElementById("lapTimes");
const $trackOptions = document.getElementById("trackOptions");
const $trackCarouselLeft = document.getElementById("trackCarouselLeft");
const $trackCarouselRight = document.getElementById("trackCarouselRight");
const $titleScreen = document.getElementById("titleScreen");
const $gameVersion = document.getElementById("gameVersion");
const $loadingDialog = document.getElementById("loadingDialog");
const $loadingDialogText = document.getElementById("loadingDialogText");
const $bestLapTimeValue = document.getElementById("bestLapTimeValue");
const $finishFlag = document.getElementById("finishFlag");
const $muteButton = document.getElementById("muteButton");

let isMuted = false;
let leaderboard;
let allLeaderboards = { weekly: null, monthly: null, allTime: null };
let currentLeaderboardTab = 0; // 0 = weekly, 1 = monthly, 2 = allTime
let leaderboardAutoCycleInterval = null;
const LEADERBOARD_TAB_NAMES = ["weekly", "monthly", "allTime"];
const LEADERBOARD_TAB_LABELS = ["Weekly", "Monthly", "All time"];
const skidCanvases = [];
const trackOptions = [];
let lastSkids = {};
const samples = [];
const grids = [];
const lapTimes = [];

// Carousel state
let carouselCurrentPage = 0;
let carouselAnimationId = null;
const TRACKS_PER_PAGE = 8; // 4 columns x 2 rows
const TRACK_ITEM_WIDTH = 320;
const TRACK_GAP = 20;
const PAGE_WIDTH = TRACK_ITEM_WIDTH * 4 + TRACK_GAP * 4;
let selectedTeamIndex = +(
  localStorage.getItem("selectedTeamIndex") ||
  Math.floor(Math.random() * TEAMS.length)
);
let $trackRoad,
  $trackRoadTarmac,
  $trackCenterline,
  $trackRacingline,
  $trackContainer;
let trackCenterlineSegments, trackCenterlineOffset;
let trackRacinglineSegments, trackRacinglineOffset;

const ctx = new (window.AudioContext || window.webkitAudioContext)();
const musicPlayer = new MusicPlayer(ctx);
const soundPlayer = new SoundPlayer(ctx);

const playerCar = createCar("player", selectedTeamIndex, true);
playerCar.isPlayer = true;

const cars = [];

const camera = {
  x: playerCar.x,
  y: playerCar.y,
  hvw,
  hvh,
};

const keyPresses = {
  ArrowUp: false,
  ArrowDown: false,
  ArrowLeft: false,
  ArrowRight: false,
  z: false,
  x: false,
};
const registeredKeys = Object.keys(keyPresses);

function createCar(carId, colorIndex, isPlayer) {
  const $car = document.createElement("div");
  $car.id = carId;
  $car.className = "car";
  $car.classList.toggle("ghost", !isPlayer);
  $car.innerHTML = `
    <div class="wheel-fl wheel"></div>
    <div class="wheel-fr wheel"></div>
  `;
  $cars.appendChild($car);

  const car = new Car(
    $car,
    carId,
    colorIndex,
    !Boolean(isPlayer),
    isPlayer ? ASSETS[engineLoopSound] : null
  );
  car.trackScale = TRACK_SCALE;
  car.x = hvw;
  car.y = hvh;

  if (isPlayer) {
    car.onBestLapTime = handlePlayerBestLapTime;
    car.onLapComplete = handlePlayerLapComplete;
  }

  return car;
}

async function initManifest() {
  for (let i = 0; i < TRACK_NAMES.length; i++) {
    const trackName = TRACK_NAMES[i];
    const assetUrl = trackImages[trackName];
    assetsManifest.push({ url: assetUrl, type: "image" });
  }
  assetsManifest.push({ url: carSpritesImg, type: "image" });
  assetsManifest.push({
    url: checkeredStripImg,
    type: "image",
  });
  assetsManifest.push({
    url: checkeredFlagImg,
    type: "image",
  });
  assetsManifest.push({ url: gameLogo, type: "image" });
  assetsManifest.push({ url: startFinishImg, type: "image" });
  assetsManifest.push({ url: startLightsImg, type: "image" });
  assetsManifest.push({ url: startingGridImg, type: "image" });
  assetsManifest.push({ url: spinnerImg, type: "image" });

  assetsManifest.push({ url: engineLoopSound, type: "arraybuffer" });
  assetsManifest.push({ url: titleMusic, type: "arraybuffer" });
  assetsManifest.push({ url: gameMusic, type: "arraybuffer" });
  assetsManifest.push({ url: startLightSound, type: "arraybuffer" });
  assetsManifest.push({ url: buttonClickSound, type: "arraybuffer" });

  const load = (item) => {
    if (item.type === "image") {
      return fetch(item.url, { cache: "reload" })
        .then((r) => r.blob())
        .then(createImageBitmap);
    }
    if (item.type === "json") {
      return fetch(item.url, { cache: "reload" }).then((r) => r.json());
    }
    if (item.type === "arraybuffer") {
      return fetch(item.url, { cache: "reload" }).then((r) => r.arrayBuffer());
    }
    if (item.type === "audio") {
      // Load as blob → object URL → HTMLAudioElement
      return fetch(item.url, { cache: "reload" })
        .then((r) => r.blob())
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const audio = new Audio();
          audio.src = objectUrl;
          audio.preload = "auto";
          return audio;
        });
    }
    return fetch(item.url, { cache: "reload" }).then((r) => r.blob());
  };

  // Progress reporting
  let done = 0;
  const total = assetsManifest.length;
  const tick = () => {
    const p = Math.round((done / total) * 100);
    bar.style.width = p + "%";
    tip.textContent = `Loading core assets... ${p}%`;
  };

  // Kick off asset fetches in parallel
  const results = {};
  await Promise.all(
    assetsManifest.map((m, i) =>
      load(m)
        .then((v) => {
          results[m.url] = v;
          done++;
          tick();
        })
        .catch((e) => {
          console.warn("Failed to load", m.url, e);
          done++;
          tick();
        })
    )
  );
  ASSETS = results;
  playerCar.setEngineSound(ASSETS[engineLoopSound]);

  if (!titleMusicBuffer) {
    titleMusicBuffer = await musicPlayer.createAudioBufferFromArrayBuffer(
      ASSETS[titleMusic]
    );
  }

  if (!gameMusicBuffer) {
    gameMusicBuffer = await musicPlayer.createAudioBufferFromArrayBuffer(
      ASSETS[gameMusic]
    );
  }

  if (!buttonClickBuffer) {
    buttonClickBuffer = await musicPlayer.createAudioBufferFromArrayBuffer(
      ASSETS[buttonClickSound]
    );
  }

  if (!startLightBuffer) {
    startLightBuffer = await musicPlayer.createAudioBufferFromArrayBuffer(
      ASSETS[startLightSound]
    );
  }

  $startGameButton.disabled = false;
  $startGameButton.addEventListener("click", handleStartGameButtonClick);
}

async function handleStartGameButtonClick() {
  soundButtonClick();
  await musicPlayer.unlock();
  await soundPlayer.unlock();
  initGame();
  update();
}

async function initGame() {
  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("keyup", handleKeyUp);

  $bestLapInput.addEventListener("input", handleBestLapInput);
  $bestLapSubmitButton.addEventListener(
    "click",
    handleBestLapSubmitButtonClick
  );
  $bestLapDialogCloseButton.addEventListener(
    "click",
    handleBestLapDialogCloseButtonClick
  );
  $bestLapShareButton.addEventListener("click", handleBestLapShareButtonClick);

  $copyFallbackCloseButton.addEventListener(
    "click",
    handleCopyFallbackCloseButtonClick
  );

  $startRaceButton.addEventListener("click", handleStartRaceButtonClick);
  $startRaceDialogCloseButton.addEventListener(
    "click",
    handleStartRaceDialogCloseButtonClick
  );
  $startRaceShareButton.addEventListener(
    "click",
    handleStartRaceShareButtonClick
  );

  // Leaderboard tab click handlers
  $startRaceLeaderboardTabs.forEach((tab) => {
    tab.addEventListener("click", handleLeaderboardTabClick);
  });

  $pauseResumeRaceButton.addEventListener("click", handleResumeRaceButtonClick);
  $pauseQuitRaceButton.addEventListener("click", handleQuitRaceButtonClick);
  $pauseDialogCloseButton.addEventListener(
    "click",
    handlePauseDialogCloseButtonClick
  );

  $randomTrackOption.addEventListener("click", handleRandomTrackOptionClick);

  $muteButton.addEventListener("click", toggleMute);

  initTrackOptions();
  initCarousel();
  initTeamOptions();
  initSparks($sparks);
  initLapTimes();
  initStartLights();

  setIsDemo(true);
  invalidateHUD();
  await loadTrack(TRACK_NAMES[0], false);
  leaderboard = await loadLeaderboard(TRACK_NAMES[0], true);
  displayLeaderboard($bestLapLeaderboard, leaderboard);
  isInit = true;

  // Hide share buttons when running on itch.io
  if (isInUncontrolledIframe()) {
    $bestLapShareButton.style.display = "none";
    $startRaceShareButton.style.display = "none";
    console.log("Running on itch.io - share buttons disabled");
  }

  // Check for deep-linked track in URL
  const urlParams = new URLSearchParams(window.location.search);
  const trackParam = urlParams.get("track");

  showTrackOptions();

  musicPlayer.play(titleMusicBuffer, {
    resume: true,
    loop: true,
    volume: 1,
  });

  const storedTier = getStoredPerfTier();

  console.log("Running performance benchmark...");
  if (storedTier) {
    applyPerfTier(storedTier);
  } else {
    setSkidEffects(true);
    setSparkEffects(true);

    const perfSamples = await runPerfBenchmark({ durationMs: 10000 });
    const tier = classifyPerf(perfSamples);
    storePerfTier(tier);
    applyPerfTier(tier);
  }

  $gameVersion.textContent = GAME_VERSION;
  console.log("Game version:", GAME_VERSION);

  if (trackParam && TRACK_NAMES.includes(trackParam)) {
    // Valid track parameter - auto-show start race dialog
    console.log(`Deep-linking to track: ${trackParam}`);
    showStartRaceDialog(trackParam);
  }
}

function applyPerfTier(tier) {
  console.log("Perf tier:", tier);

  if (tier === "high") {
    setSkidEffects(true);
    setSparkEffects(true);
  } else if (tier === "medium") {
    setSkidEffects(false);
    setSparkEffects(true);
  } else {
    setSkidEffects(false);
    setSparkEffects(false);
  }
}

function initTrackOptions() {
  for (let i = 0; i < TRACK_NAMES.length; i++) {
    const $trackOption = document.createElement("div");
    const trackName = TRACK_NAMES[i];
    const assetUrl = trackImages[trackName];
    $trackOption.style.backgroundImage = `url(${assetUrl})`;
    const [track, region] = TRACK_DISPLAY_NAMES[trackName].split(",");
    $trackOption.setAttribute("trackName", trackName);
    $trackOption.innerHTML = `<span>${track}</span><span>${region}</span>`;
    $trackOptions.appendChild($trackOption);
    trackOptions.push($trackOption);
    $trackOption.addEventListener("click", handleTrackOptionClick);
  }
}

// Easing function for smooth animation
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Smooth scroll carousel to a specific page
function scrollToPage(page) {
  if (carouselAnimationId !== null) {
    cancelAnimationFrame(carouselAnimationId);
  }

  const startScroll = parseFloat(
    $trackOptions.style.transform?.match(/-?\d+(\.\d+)?/)?.[0] || 0
  );
  const targetScroll = -page * PAGE_WIDTH;
  const duration = 400; // ms
  const startTime = performance.now();

  function animate(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeInOutCubic(progress);

    const currentScroll =
      startScroll + (targetScroll - startScroll) * easedProgress;
    $trackOptions.style.transform = `translateX(${currentScroll}px)`;

    if (progress < 1) {
      carouselAnimationId = requestAnimationFrame(animate);
    } else {
      carouselAnimationId = null;
      carouselCurrentPage = page;
      updateCarouselButtons();
    }
  }

  carouselAnimationId = requestAnimationFrame(animate);
}

// Update carousel button states based on current page
function updateCarouselButtons() {
  const totalItems = TRACK_NAMES.length + 1; // +1 for random option
  const totalPages = Math.ceil(totalItems / TRACKS_PER_PAGE);

  $trackCarouselLeft.disabled = carouselCurrentPage === 0;
  $trackCarouselRight.disabled = carouselCurrentPage >= totalPages - 1;
}

// Initialize carousel
function initCarousel() {
  carouselCurrentPage = 0;
  $trackOptions.style.transform = "translateX(0px)";
  updateCarouselButtons();

  $trackCarouselLeft.addEventListener("click", () => {
    // Ignore clicks while animation is in progress
    if (carouselAnimationId !== null) return;

    soundButtonClick();
    if (carouselCurrentPage > 0) {
      scrollToPage(carouselCurrentPage - 1);
    }
  });

  $trackCarouselRight.addEventListener("click", () => {
    // Ignore clicks while animation is in progress
    if (carouselAnimationId !== null) return;

    soundButtonClick();
    if (
      carouselCurrentPage <
      Math.ceil((TRACK_NAMES.length + 1) / TRACKS_PER_PAGE) - 1
    ) {
      scrollToPage(carouselCurrentPage + 1);
    }
  });
}

function initTeamOptions() {
  for (let i = 0; i < TEAMS.length; i++) {
    const $teamOption = document.createElement("div");
    $teamOption.className = "team-option";
    const team = TEAMS[i];
    const $car = document.createElement("div");
    $car.className = "car";
    $car.innerHTML = `
      <div class="wheel-fl wheel"></div>
      <div class="wheel-fr wheel"></div>
    `;
    $car.style.backgroundPositionX = `${i * -74}px`;
    $teamOption.setAttribute("data-team-index", i);
    $teamOption.classList.toggle("selected", selectedTeamIndex === i);
    $teamOption.appendChild($car);
    const $teamName = document.createElement("div");
    $teamName.className = "team-name";
    $teamName.innerText = team.name;
    $teamOption.appendChild($teamName);
    $startRaceTeamOptions.appendChild($teamOption);
    $teamOption.addEventListener("click", handleTeamOptionClick);
  }
}

function handleTeamOptionClick(event) {
  soundButtonClick();
  const teamIndex = +event.currentTarget.getAttribute("data-team-index");
  selectedTeamIndex = teamIndex;
  localStorage.setItem("selectedTeamIndex", selectedTeamIndex);
  const $teamOptions = $startRaceTeamOptions.querySelectorAll(".team-option");
  for (let i = 0; i < $teamOptions.length; i++) {
    $teamOptions[i].classList.toggle("selected", i === teamIndex);
  }

  if (playerCar) {
    playerCar.setColor(selectedTeamIndex);
  }
}

function showLoadingSpinner(text) {
  $loadingDialogText.innerText = text;
  $loadingDialog.style.visibility = "visible";
}

function hideLoadingSpinner() {
  $loadingDialog.style.visibility = "hidden";
}

function showTrackOptions() {
  $trackOptions.classList.remove("show"); // reset
  void $trackOptions.offsetWidth; // force reflow so animation restarts
  $trackOptions.classList.add("show");
}

function handleTrackOptionClick(event) {
  soundButtonClick();
  const trackName = event.currentTarget.getAttribute("trackName");
  showStartRaceDialog(trackName);
}

function handleRandomTrackOptionClick() {
  handleTrackOptionClick({
    currentTarget:
      trackOptions[Math.floor(Math.random() * trackOptions.length)],
  });
}

function showPauseDialog() {
  isPaused = true;
  lastPausedTime = Date.now();
  $pauseDialog.style.visibility = "visible";
  $pauseDialog.classList.toggle("hidden", false);
}

function hidePauseDialog() {
  isPaused = false;
  if (lastPausedTime >= 0) {
    raceStartTime += Date.now() - lastPausedTime;
  }
  lastPausedTime = -1;
  resetPhysicsTiming(); // Reset physics timing to prevent stale delta
  $pauseDialog.style.visibility = "hidden";
  $pauseDialog.classList.toggle("hidden", true);
}

function handleQuitRaceButtonClick() {
  soundButtonClick();
  showTitleScreen();
  hidePauseDialog();
}

function handleResumeRaceButtonClick() {
  soundButtonClick();
  hidePauseDialog();
}

function handlePauseDialogCloseButtonClick() {
  soundButtonClick();
  hidePauseDialog();
}

async function showBestLapDialog(bestLapTime) {
  // Clear cache to get fresh data
  clearLeaderboardCache();

  // Load all leaderboards
  allLeaderboards = await loadAllLeaderboards(currentTrackName);

  // Check qualification in priority order: All-time, Monthly, Weekly
  const allTimeRank = getLapTimeRank(allLeaderboards.allTime, bestLapTime);
  const monthlyRank = getLapTimeRank(allLeaderboards.monthly, bestLapTime);
  const weeklyRank = getLapTimeRank(allLeaderboards.weekly, bestLapTime);

  let selectedLeaderboard;
  let selectedRank;
  let selectedLabel;

  if (allTimeRank >= 0) {
    selectedLeaderboard = allLeaderboards.allTime;
    selectedRank = allTimeRank;
    selectedLabel = "All time";
  } else if (monthlyRank >= 0) {
    selectedLeaderboard = allLeaderboards.monthly;
    selectedRank = monthlyRank;
    selectedLabel = "Monthly";
  } else if (weeklyRank >= 0) {
    selectedLeaderboard = allLeaderboards.weekly;
    selectedRank = weeklyRank;
    selectedLabel = "Weekly";
  } else {
    // Doesn't qualify for any leaderboard, show all-time
    selectedLeaderboard = allLeaderboards.allTime;
    selectedRank = -1;
    selectedLabel = "All time";
  }

  // Set the leaderboard reference for validation
  leaderboard = selectedLeaderboard;

  // Display the selected leaderboard
  displayLeaderboard($bestLapLeaderboard, selectedLeaderboard);
  $bestLapLeaderboardType.innerText = selectedLabel;

  resetBestLapDialog($bestLapLeaderboard);

  const qualifies = selectedRank >= 0;
  if (qualifies) {
    insertLeaderboardEntryAt($bestLapLeaderboard, selectedRank, {
      teamIndex: playerCar.colorIndex,
      name3: "",
      lapMs: bestLapTime,
    });

    $bestLapInput.value = window.localStorage.getItem("name3") || "";
    setSelectedEntryName($bestLapLeaderboard, $bestLapInput.value);
  }

  $bestLapTimeValue.setAttribute("data-value", bestLapTime);
  $bestLapTimeValue.innerText = formatLapTime(bestLapTime);

  $bestLapDialog.querySelectorAll(".good-enough").forEach(($el) => {
    $el.classList.toggle("hidden", !qualifies);
  });
  $bestLapDialog.querySelectorAll(".not-good-enough").forEach(($el) => {
    $el.classList.toggle("hidden", qualifies);
  });

  $bestLapDialog.setAttribute("data-step", "entering");

  $bestLapDialog.style.visibility = "visible";
  $bestLapDialog.classList.toggle("hidden", false);

  $bestLapInput.focus();

  validateBestLapSubmission();
}

function hideBestLapDialog() {
  $bestLapDialog.style.visibility = "hidden";
  $bestLapDialog.classList.toggle("hidden", true);
}

function soundButtonClick() {
  soundPlayer.playOnce(buttonClickBuffer, {
    volume: 0.5,
  });
}

function soundStartLight() {
  soundPlayer.playOnce(startLightBuffer, {
    volume: 1,
  });
}

function toggleMute() {
  isMuted = !isMuted;

  if (isMuted) {
    musicPlayer.setVolume(0);
    soundPlayer.setMasterVolume(0);
    if (playerCar.engineSound && playerCar.engineSound.engineGain) {
      playerCar.engineSound.engineGain.gain.value = 0;
    }
  } else {
    musicPlayer.setVolume(1);
    soundPlayer.setMasterVolume(1);
    if (playerCar.engineSound && playerCar.engineSound.engineGain) {
      playerCar.engineSound.engineGain.gain.value = 0.25;
    }
  }

  updateMuteButton();
}

function updateMuteButton() {
  if ($muteButton) {
    $muteButton.innerHTML = isMuted ? "&#x1F507;" : "&#x1F50A;";
    $muteButton.setAttribute("aria-label", isMuted ? "Unmute" : "Mute");
  }
}

async function handleBestLapSubmitButtonClick() {
  soundButtonClick();
  window.localStorage.setItem("name3", $bestLapInput.value);

  $bestLapDialog.setAttribute("data-step", "submitting");

  const result = await submitBestLapTime({
    trackId: currentTrackName,
    name3: $bestLapInput.value,
    teamIndex: playerCar.colorIndex,
    samples: getLocalGhostSamples(),
  });
  leaderboard = result.leaderboard;
  $bestLapDialog.setAttribute("data-step", "submitted");
}

function handleBestLapInput(event) {
  const input = event.target;
  // Remove any characters that are not letters or numbers
  input.value = input.value.replace(/[^A-Za-z0-9]/g, "");
  $bestLapSubmitButton.setAttribute("disable", input.value.length !== 3);
  setSelectedEntryName($bestLapLeaderboard, input.value);

  validateBestLapSubmission();
}

function validateBestLapSubmission() {
  const inputName = $bestLapInput.value.toLowerCase();
  let isDuplicateName = false;
  const bestLapMs = +$bestLapTimeValue.getAttribute("data-value");
  let validState = "valid";

  for (let i = 0; i < leaderboard.length; i++) {
    const entry = leaderboard[i];
    if (entry.name3 === inputName) {
      if (bestLapMs < entry.lapMs) {
        validState = "faster";
      } else {
        validState = "slower";
      }
    }
  }

  switch (validState) {
    case "faster":
      $bestLapInputMessage.innerText =
        "Your lap time will replace an existing entry.";
      break;

    case "slower":
      $bestLapInputMessage.innerText = "You can't submit a slower lap time.";
      break;

    default:
      $bestLapInputMessage.innerText = "";
      break;
  }
  $bestLapDialog.setAttribute("data-validation", validState);
  $bestLapSubmitButton.disabled = validState === "slower";
}

function handleBestLapDialogCloseButtonClick() {
  soundButtonClick();
  showTitleScreen();
  hideBestLapDialog();
}

async function handleBestLapShareButtonClick() {
  soundButtonClick();

  const trackName = currentTrackName;
  const url = `${window.location.origin}${window.location.pathname}?track=${trackName}`;

  const result = await copyToClipboard(url);

  if (result.success) {
    // Temporarily change button text to show success
    const originalText = $bestLapShareButton.textContent;
    $bestLapShareButton.textContent = "Link copied!";

    setTimeout(() => {
      $bestLapShareButton.textContent = originalText;
    }, 2000);
  } else {
    // Show manual copy fallback dialog
    showCopyFallbackDialog(url);
  }
}

async function showStartRaceDialog(trackName) {
  // show track
  selectedTrackName = trackName;
  const assetUrl = trackImages[trackName];
  $startRaceTrackInfo.style.backgroundImage = `url(${assetUrl})`;
  const [track, region] = TRACK_DISPLAY_NAMES[trackName].split(",");
  $startRaceTrackTitle.innerHTML = `<span>${track}</span><span>${region}</span>`;

  $startRaceDialog.style.visibility = "visible";
  $startRaceDialog.classList.toggle("hidden", false);

  // Show loading state for all leaderboards
  displayLoadingLeaderboard($startRaceLeaderboardWeekly);
  displayLoadingLeaderboard($startRaceLeaderboardMonthly);
  displayLoadingLeaderboard($startRaceLeaderboardAllTime);

  // Reset to weekly tab (index 0)
  currentLeaderboardTab = 0;
  setLeaderboardTab(0, false);

  // Clear cache to get fresh data
  clearLeaderboardCache();

  // Load all leaderboards
  allLeaderboards = await loadAllLeaderboards(trackName);

  // Display all leaderboards
  displayLeaderboard($startRaceLeaderboardWeekly, allLeaderboards.weekly);
  displayLeaderboard($startRaceLeaderboardMonthly, allLeaderboards.monthly);
  displayLeaderboard($startRaceLeaderboardAllTime, allLeaderboards.allTime);

  // Set the main leaderboard reference to allTime for ghost loading
  leaderboard = allLeaderboards.allTime;
  displayLeaderboard($bestLapLeaderboard, leaderboard);

  // Start auto-cycle
  startLeaderboardAutoCycle();
}

function setLeaderboardTab(tabIndex, animate = true) {
  currentLeaderboardTab = tabIndex;

  // Update tab active states
  $startRaceLeaderboardTabs.forEach((tab, index) => {
    tab.classList.toggle("active", index === tabIndex);
  });

  // Slide the leaderboard
  const offset = -tabIndex * 470;
  if (animate) {
    $startRaceLeaderboardSlider.style.transition = "transform 0.4s ease-in-out";
  } else {
    $startRaceLeaderboardSlider.style.transition = "none";
  }
  $startRaceLeaderboardSlider.style.transform = `translateX(${offset}px)`;
}

function cycleToNextLeaderboardTab() {
  const nextTab = (currentLeaderboardTab + 1) % 3;
  setLeaderboardTab(nextTab, true);
}

function startLeaderboardAutoCycle() {
  stopLeaderboardAutoCycle();
  leaderboardAutoCycleInterval = setInterval(cycleToNextLeaderboardTab, 5000);
}

function stopLeaderboardAutoCycle() {
  if (leaderboardAutoCycleInterval) {
    clearInterval(leaderboardAutoCycleInterval);
    leaderboardAutoCycleInterval = null;
  }
}

function handleLeaderboardTabClick(event) {
  soundButtonClick();
  stopLeaderboardAutoCycle();

  const tabName = event.currentTarget.getAttribute("data-tab");
  const tabIndex = LEADERBOARD_TAB_NAMES.indexOf(tabName);
  if (tabIndex >= 0) {
    setLeaderboardTab(tabIndex, true);
  }
}

function hideStartRaceDialog() {
  stopLeaderboardAutoCycle();
  $startRaceDialog.style.visibility = "hidden";
  $startRaceDialog.classList.toggle("hidden", true);
}

async function handleStartRaceButtonClick() {
  soundButtonClick();
  setIsDemo(false);
  hideStartRaceDialog();
  hideTitleScreen();
  await loadTrack(selectedTrackName, true);
  invalidateHUD();
  musicPlayer.play(gameMusicBuffer, {
    resume: true,
    loop: true,
    volume: 0.5,
  });
}

function handleStartRaceDialogCloseButtonClick() {
  hideStartRaceDialog();
}

async function handleStartRaceShareButtonClick() {
  soundButtonClick();

  const trackName = selectedTrackName;
  const url = `${window.location.origin}?track=${trackName}`;

  const result = await copyToClipboard(url);

  if (result.success) {
    // Temporarily change button text to show success
    const originalText = $startRaceShareButton.textContent;
    $startRaceShareButton.textContent = "Link copied!";

    setTimeout(() => {
      $startRaceShareButton.textContent = originalText;
    }, 2000);
  } else {
    // Show manual copy fallback dialog
    showCopyFallbackDialog(url);
  }
}

let copyFallbackTimeout = null;

function showCopyFallbackDialog(url) {
  $copyFallbackInput.value = url;
  $copyFallbackDialog.style.visibility = "visible";
  $copyFallbackDialog.classList.toggle("hidden", false);

  // Auto-select the text for easy manual copying
  setTimeout(() => {
    $copyFallbackInput.select();
    $copyFallbackInput.focus();
  }, 100);

  // Auto-dismiss after 10 seconds
  if (copyFallbackTimeout) {
    clearTimeout(copyFallbackTimeout);
  }
  copyFallbackTimeout = setTimeout(() => {
    hideCopyFallbackDialog();
  }, 10000);
}

function hideCopyFallbackDialog() {
  if (copyFallbackTimeout) {
    clearTimeout(copyFallbackTimeout);
    copyFallbackTimeout = null;
  }
  $copyFallbackDialog.style.visibility = "hidden";
  $copyFallbackDialog.classList.toggle("hidden", true);
}

function handleCopyFallbackCloseButtonClick() {
  soundButtonClick();
  hideCopyFallbackDialog();
}

function showTitleScreen() {
  $titleScreen.style.display = "flex";

  setIsDemo(true);
  invalidateHUD();
  musicPlayer.play(titleMusicBuffer, {
    resume: true,
    loop: true,
    volume: 1,
  });
}

function hideTitleScreen() {
  $titleScreen.style.display = "none";
}

async function loadGhosts() {
  cars.length = 0;
  $cars.innerHTML = "";

  if (leaderboard) {
    const ghostEntries = [];
    for (let i = 0; i < Math.min(leaderboard.length, 9); i++) {
      const entry = leaderboard[i];
      if (entry.lapMs >= 0 && Boolean(entry.path)) {
        ghostEntries.push(entry);
      }
    }

    // Use allSettled instead of all to allow partial failures
    const results = await Promise.allSettled(
      ghostEntries.map((entry) => loadGhostBinary(entry.path))
    );

    // Track how many ghosts loaded successfully
    let loadedCount = 0;
    let failedCount = 0;

    for (let i = 0; i < ghostEntries.length; i++) {
      const entry = ghostEntries[i];
      const result = results[i];

      // Only create ghost car if loading succeeded and data exists
      if (
        result.status === "fulfilled" &&
        result.value &&
        loadedGhosts[entry.path]
      ) {
        const ghostCar = createCar(`ghost_${i}`, entry.teamIndex, false);
        ghostCar.setGhostData(loadedGhosts[entry.path].samples);
        cars.push(ghostCar);
        loadedCount++;
      } else {
        failedCount++;
      }
    }

    if (failedCount > 0) {
      console.warn(
        `Loaded ${loadedCount} ghost(s), ${failedCount} failed to load. Race will continue without failed ghosts.`
      );
    }
  }

  $cars.appendChild(playerCar.$el);
  cars.push(playerCar);
}

function initLapTimes() {
  $lapTimes.innerHTML = "";
  $lapTimes.classList.toggle("hidden", isDemo || lapCount <= 1);
  lapTimes.length = 0;
  for (let i = 0; i < lapCount; i++) {
    const $lapTime = document.createElement("li");
    $lapTime.innerText = formatLapTime(-1);
    lapTimes.push($lapTime);
    $lapTimes.appendChild($lapTime);
  }
}

function initStartLights() {
  $startLights.innerHTML = "";
  $startLights.lights = [];
  for (let i = 0; i < 5; i++) {
    const $startLight = document.createElement("div");
    $startLights.lights.push($startLight);
    $startLights.appendChild($startLight);
  }
}

function setStartLight(count) {
  if (count >= 1 && count <= 5 && currentStartLightCount !== count) {
    currentStartLightCount = count;
    soundStartLight();
  }

  for (let i = 0; i < $startLights.lights.length; i++) {
    $startLights.lights[i].style.backgroundPositionX =
      i < count ? "-104px" : "0";
  }
}

function setRacingLineVisible(value = isRacingLineVisible) {
  isRacingLineVisible = value;
  $trackRacingline.style.opacity = isRacingLineVisible ? 1 : 0;
  $samples.style.opacity = isRacingLineVisible ? 1 : 0;
}

async function loadTrack(trackName, showSpinner = true) {
  if (showSpinner) showLoadingSpinner("Loading race...");

  currentStartLightCount = -1;

  currentTrackName = trackName || TRACK_NAMES[0];
  $trackLabel.innerText = TRACK_DISPLAY_NAMES[currentTrackName];
  await loadSVG(trackSvgs[currentTrackName], $track);

  lastSkids = {};
  clearSkids();
  resetRace();

  $track.querySelector("svg").style.transform = `scale(${TRACK_SCALE})`;
  $trackRoad = $track.querySelector("svg #road");
  $trackRoadTarmac = $track.querySelector("svg #tarmac");

  $trackCenterline = $track.querySelector("svg #centerline");
  $trackRacingline =
    $track.querySelector("svg #racingline") || $trackCenterline;
  $trackContainer = $track.querySelector("svg #trackContainer");
  setRacingLineVisible();

  const trackContainerTransform = $trackContainer.getAttribute("transform");
  trackCenterlineOffset = extractTranslate(trackContainerTransform);
  trackCenterlineSegments = samplePathPoints(
    $trackCenterline,
    5,
    trackCenterlineOffset,
    TRACK_SCALE
  );
  trackRacinglineSegments = samplePathPoints(
    $trackRacingline || $trackCenterline,
    5,
    trackCenterlineOffset,
    TRACK_SCALE
  );

  renderTrackRacingline();

  let startingGridSegmentIndex = startLine - 3;
  if (startingGridSegmentIndex < 0)
    startingGridSegmentIndex += trackCenterlineSegments.length;

  placeStartLine(startLine);
  placeFinishLine(finishLine);
  renderGrids(startingGridSegmentIndex);
  await loadGhosts();

  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];
    car.$trackRoad = $trackRoad;
    car.$trackRoadTarmac = $trackRoadTarmac;
    car.finishLine = finishLine;
    car.camera = camera;
    car.setTrackRacinglineSegments(trackRacinglineSegments);
    car.setStartingGrid(grids[i]);
  }

  generateSkids();
  startRace();

  document.getElementById("loadingScreen").style.display = "none";
  hideLoadingSpinner();
}

function setIsDemo(value) {
  isDemo = value;
  playerCar.setBot(isDemo);
  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];
    if (!car.isPlayer) {
      car.setVisible(!isDemo);
    }
  }
}

function invalidateHUD() {
  $startLights.classList.toggle("hidden", isDemo);
  $viz.classList.toggle("hidden", isDemo);
  $lapTimes.classList.toggle("hidden", isDemo || lapCount <= 1);
}

function resetRace() {
  isRaceStarted = false;
  isRaceFinished = false;
  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];
    car.reset();
  }
}

function startRace() {
  clearLocalGhost();
  isRaceStarted = true;
  raceStartTime = Date.now() + (isDemo ? 0 : 6000);
  resetPhysicsTiming();

  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];
    car.startRace();
  }
}

function generateSkids() {
  const colCount = ($track.offsetWidth * TRACK_SCALE) / SKID_CANVAS_WIDTH;
  const rowCount = ($track.offsetHeight * TRACK_SCALE) / SKID_CANVAS_HEIGHT;
  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < colCount; c++) {
      const $skidCanvas = document.createElement("canvas");
      $skidCanvas.width = SKID_CANVAS_WIDTH;
      $skidCanvas.height = SKID_CANVAS_HEIGHT;
      $skidCanvas.style.transform = `translate(${c * SKID_CANVAS_WIDTH}px, ${
        r * SKID_CANVAS_HEIGHT
      }px)`;
      const ctx = $skidCanvas.getContext("2d");
      $skidCanvas.ctx = ctx;
      ctx.translate(-c * SKID_CANVAS_WIDTH, -r * SKID_CANVAS_HEIGHT);
      ctx.strokeStyle = "rgba(0, 0, 0, 0.3)";
      ctx.lineWidth = 12;
      $skids.appendChild($skidCanvas);
      skidCanvases.push($skidCanvas);
    }
  }
}

function getSkidCanvas(x, y) {
  const colCount = ($track.offsetWidth * TRACK_SCALE) / SKID_CANVAS_WIDTH;
  const c = Math.floor(x / SKID_CANVAS_WIDTH);
  const r = Math.floor(y / SKID_CANVAS_HEIGHT);
  const i = r * colCount + c;
  return skidCanvases[i];
}

function clearSkids() {
  for (let i = 0; i < skidCanvases.length; i++) {
    const canvas = skidCanvases[i];
    const ctx = canvas.ctx;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  }
}

function drawSkid(id, x, y, onlyOffroad = true) {
  if (!showSkidEffects) return;

  const $skidCanvas = getSkidCanvas(x, y);
  if (!lastSkids[id]) {
    lastSkids[id] = { x, y };
    return;
  }
  const dx = x - lastSkids[id].x;
  const dy = y - lastSkids[id].y;
  if (dx * dx + dy * dy > SKID_INTERVAL_SQ) {
    const ctx = $skidCanvas.ctx;
    const isOffroad =
      !onlyOffroad ||
      !isPositionInPath(
        $trackRoad,
        $trackRoadTarmac,
        x - camera.x + hvw,
        y - camera.y + hvh
      );
    if (isOffroad) {
      ctx.beginPath();
      ctx.moveTo(lastSkids[id].x, lastSkids[id].y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    lastSkids[id].x = x;
    lastSkids[id].y = y;
  }
}

function drawTireSkid(player) {
  if (now < raceStartTime) return;
  const dv = directionVectors(player.heading);
  const wheelOffsetH = 1.3 * TRACK_SCALE;
  const wheelOffsetV = 3 * TRACK_SCALE;
  const rearWheelLeftX =
    player.x + dv.left.x * wheelOffsetH + dv.backward.x * wheelOffsetV;
  const rearWheelLeftY =
    player.y + dv.left.y * wheelOffsetH + dv.backward.y * wheelOffsetV;
  const rearWheelRightX =
    player.x + dv.right.x * wheelOffsetH + dv.backward.x * wheelOffsetV;
  const rearWheelRightY =
    player.y + dv.right.y * wheelOffsetH + dv.backward.y * wheelOffsetV;
  drawSkid(`${player.id}_wrl`, rearWheelLeftX, rearWheelLeftY);
  drawSkid(`${player.id}_wrr`, rearWheelRightX, rearWheelRightY);
}

function placeStartLine(trackSegmentIndex) {
  $startLine.x = trackCenterlineSegments[trackSegmentIndex].x;
  $startLine.y = trackCenterlineSegments[trackSegmentIndex].y;
  const angle = segmentAngle(trackCenterlineSegments, trackSegmentIndex);
  $startLine.style.transform = `translate(${$startLine.x}px, ${$startLine.y}px) rotate(${angle}rad)`;
}

function placeFinishLine(trackSegmentIndex) {
  $finishLine.x = trackCenterlineSegments[trackSegmentIndex].x;
  $finishLine.y = trackCenterlineSegments[trackSegmentIndex].y;
  const angle = segmentAngle(trackCenterlineSegments, trackSegmentIndex);
  $finishLine.style.transform = `translate(${$finishLine.x}px, ${$finishLine.y}px) rotate(${angle}rad)`;
}

function renderTrackRacingline() {
  $samples.innerHTML = "";
  samples.length = 0;
  trackRacinglineSegments.forEach((point) => {
    const sample = document.createElement("div");
    sample.style.transform = `translate(${point.x}px, ${point.y}px)`;
    $samples.appendChild(sample);
    samples.push(sample);
  });
}

function renderGrids(trackSegmentIndex) {
  $gridContainer.innerHTML = "";
  grids.length = 0;
  for (let i = 0; i > -10; i--) {
    const segmentIndex = trackSegmentIndex + i + Math.floor((i + 1) / 2) * 2;
    const grid = document.createElement("div");
    const angle = segmentAngle(trackCenterlineSegments, segmentIndex);
    const point = trackCenterlineSegments[segmentIndex];
    const dv = directionVectors(angle);
    grid.x = point.x + (i % 2 === 0 ? dv.left.x : dv.right.x) * 5 * TRACK_SCALE;
    grid.y = point.y + (i % 2 === 0 ? dv.left.y : dv.right.y) * 5 * TRACK_SCALE;
    grid.angle = angle;
    grid.style.transform = `translate(${grid.x}px, ${grid.y}px) rotate(${grid.angle}rad)`;
    $gridContainer.appendChild(grid);
    grids.push(grid);
  }
}

function setSkidEffects(value) {
  showSkidEffects = value;
  updateEffectsDisplay();
}

function setSparkEffects(value) {
  showSparkEffects = value;
  updateEffectsDisplay();
}

function updateEffectsDisplay() {
  $skids.classList.toggle("hidden", !showSkidEffects);
  $sparks.classList.toggle("hidden", !showSparkEffects);
}

async function handleKeyDown(event) {
  if (registeredKeys.includes(event.key)) {
    keyPresses[event.key] = true;
  }

  if (event.ctrlKey && event.shiftKey) {
    const key = event.key.toLowerCase();
    // // car color selection
    // if (key === ",") {
    //   playerCar.choosePreviousCarColor();
    // } else if (event.key === ".") {
    //   playerCar.chooseNextCarColor();
    // }

    // // track selection
    // if (!Number.isNaN(+key) && +key < TRACK_NAMES.length) {
    //   loadTrack(TRACK_NAMES[+event.key - 1]);
    // }

    // // toggle auto-pilot
    // if (key === "`") {
    //   playerCar.control.isBot = !playerCar.control.isBot;
    // }

    // toggle racing line visibility
    if (key === "r") {
      setRacingLineVisible(!isRacingLineVisible);
    }

    // clear skid lines
    if (key === "s") {
      clearSkids();
    }

    // test best lap dialog
    if (key === "l") {
      if ($bestLapDialog.style.visibility === "visible") {
        hideBestLapDialog();
      } else {
        showBestLapDialog(40000 + Math.floor(Math.random() * 9000));
      }
    }

    // // test start race dialog
    // if (key === "t") {
    //   if ($startRaceDialog.style.visibility === "visible") {
    //     hideStartRaceDialog();
    //   } else {
    //     showStartRaceDialog(currentTrackName);
    //   }
    // }

    // // fire confetti
    // if (key === "f") {
    //   showFinishFlag();
    // }

    // // test save best lap
    // if (key === "b") {
    //   const ghostData = loadLocalGhost(currentTrackName);
    //   try {
    //     const result = await submitBestLapTime({
    //       trackId: currentTrackName,
    //       name3: "TEN",
    //       teamIndex: 2,
    //       samples: ghostData.samples,
    //     });
    //     leaderboard = result.leaderboard;
    //     displayLeaderboard($bestLapLeaderboard, leaderboard);
    //   } catch (error) {
    //     console.warn(error);
    //   }
    // }
  }

  // close all dialogs
  if (event.key === "Escape") {
    if ($bestLapDialog.style.visibility === "visible") {
      hideBestLapDialog();
    }
    if ($startRaceDialog.style.visibility === "visible") {
      hideStartRaceDialog();
    }
    if ($pauseDialog.style.visibility === "visible") {
      hidePauseDialog();
    } else {
      if (!isDemo && isRaceStarted) {
        showPauseDialog();
      }
    }
  }

  // toggle sound mute (skip if typing in an input field)
  if (
    (event.key === "m" || event.key === "M") &&
    document.activeElement?.tagName !== "INPUT"
  ) {
    toggleMute();
  }
}

async function showFinishFlag() {
  isRaceFinished = true;
  playerCar.setBot(true);

  $finishFlag.classList.remove("show"); // reset
  void $finishFlag.offsetWidth; // force reflow so animation restarts
  $finishFlag.classList.add("show");

  await delay(500);
  fireConfetti();
}

function hideFinishFlag() {
  $finishFlag.classList.remove("show"); // reset
}

function handleKeyUp(event) {
  if (registeredKeys.includes(event.key)) {
    keyPresses[event.key] = false;
  }
}

function processKeyPresses() {
  if (now < raceStartTime || isRaceFinished) return;

  const playerControl = playerCar.control;

  // break
  playerControl.isBreak = keyPresses.ArrowDown || keyPresses.x;

  // accellerate
  playerControl.isAccelerate =
    !playerControl.isBreak && (keyPresses.ArrowUp || keyPresses.z);

  // steer left/right - just set flags, actual steering applied in physics tick
  playerControl.isSteeringLeft = keyPresses.ArrowLeft;
  playerControl.isSteeringRight = keyPresses.ArrowRight;
}

function updateViz(now) {
  const playerControl = playerCar.control;

  $steeringAccViz.style.transform = `scaleX(${
    playerControl.steeringAcc / playerControl.maxSteeringAcc
  })`;
  $steeringViz.style.transform = `scaleX(${playerControl.steering})`;
  $accelerationViz.style.transform = `scaleX(${
    playerControl.acceleration / playerControl.maxAcceleration
  })`;
  $speedViz.style.transform = `scaleX(${
    playerControl.speed / playerCar.getMaxSpeed()
  })`;
  $lapProgressViz.style.transform = `scaleX(${
    playerCar.lapProgress / trackRacinglineSegments.length
  })`;

  $speedLabel.innerText = Math.floor(playerControl.speed * 10);
  $lapLabel.innerText = playerCar.lap + 1;
  const lapTime =
    playerCar.lapStartTimes[playerCar.lap] >= 0
      ? now - raceStartTime - playerCar.lapStartTimes[playerCar.lap]
      : 0;
  $lapTimeLabel.innerText = formatLapTime(lapTime >= 0 ? lapTime : 0);

  $startLights.style.opacity = now > -1 && now < raceStartTime ? 1 : 0;
  const startLightCount =
    now < raceStartTime - 6000 || now > raceStartTime
      ? 0
      : Math.floor(5 - ((raceStartTime - 1000 - now) / 5000) * 5);
  setStartLight(startLightCount);
}

function highlightRacinglinePoint(index) {
  for (let i = 0; i < samples.length; i++) {
    samples[i].style.background = i === index ? "orange" : "gray";
  }
}

function updateLapTimesBoard() {
  if (lapCount <= 1) return;

  for (let i = 0; i < lapCount; i++) {
    const $lapTime = lapTimes[i];
    $lapTime.innerText = formatLapTime(playerCar.getLapTime(i));
  }
}

function updateCamera(alpha) {
  // Guard against uninitialized prevX/prevY or large position jumps (teleportation)
  const dx = playerCar.x - playerCar.prevX;
  const dy = playerCar.y - playerCar.prevY;
  const distSq = dx * dx + dy * dy;
  const TELEPORT_THRESHOLD_SQ = 10000; // ~100 units - larger than any normal frame movement

  let cameraX, cameraY;
  if (playerCar.prevX === undefined || playerCar.prevY === undefined || distSq > TELEPORT_THRESHOLD_SQ) {
    // Skip interpolation during initialization or teleportation
    cameraX = playerCar.x;
    cameraY = playerCar.y;
  } else {
    // Normal interpolation for smooth following
    cameraX = playerCar.prevX + (playerCar.x - playerCar.prevX) * alpha;
    cameraY = playerCar.prevY + (playerCar.y - playerCar.prevY) * alpha;
  }

  camera.x = cameraX;
  camera.y = cameraY;
  $game.style.transform = `translate(${(-camera.x + hvw) * cameraZoom}px, ${
    (-camera.y + hvh) * cameraZoom
  }px) scale(${cameraZoom})`;
}

function updateCarsPhysics(now) {
  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];
    // Skip ghost cars only when they're actively replaying
    // During bot phase (driving from grid to start line), run physics
    if (!car.ghostData || !car.isGhostReplayActive) {
      car.physicsTick(now);
    }
  }
}

function updateGhosts(now) {
  // Ghost cars use time-based interpolation, updated every render frame
  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];
    if (car.ghostData) {
      car.updateGhost(now);
    }
  }
}

function renderCars(alpha) {
  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];
    car.render(alpha);
  }
  // Update engine sound in render loop for smooth audio
  playerCar.updateEngineSound();
}

function recordGhostIfNeeded(now) {
  if (
    !isDemo &&
    playerCar.lapStartTimes[playerCar.lap] >= 0 &&
    now >= playerCar.lapStartTimes[playerCar.lap]
  ) {
    recordLocalGhost(
      now - playerCar.lapStartTimes[playerCar.lap],
      playerCar.x,
      playerCar.y,
      playerCar.heading
    );
  }
}

function updateCarsVisuals() {
  if (playerCar.racinglinePoint) {
    highlightRacinglinePoint(playerCar.racinglinePoint.index);
  }
  drawTireSkid(playerCar);
}

function handlePlayerBestLapTime() {
  if (!isDemo) {
    saveLocalGhost(currentTrackName);
  }
}

async function handlePlayerLapComplete() {
  if (!isDemo) {
    updateLapTimesBoard();

    // when finished
    if (playerCar.getLapCount() >= lapCount) {
      await showFinishFlag();
      await delay(2000);

      hideFinishFlag();
      showBestLapDialog(playerCar.getBestLapTime());
    }
  }
}

function updateFPS() {
  frameCount++;
  const currentTime = now;

  if (currentTime - lastFpsTime >= 1000) {
    fps = Math.round((frameCount * 1000) / (currentTime - lastFpsTime));
    frameCount = 0;
    lastFpsTime = currentTime;

    // Update FPS display with color coding
    $fpsCounter.textContent = `${fps} FPS`;
    if (fps >= 50) {
      $fpsCounter.style.color = "#00ff88";
      $fpsCounter.style.borderColor = "rgba(0, 255, 136, 0.3)";
    } else if (fps >= 30) {
      $fpsCounter.style.color = "#ffaa00";
      $fpsCounter.style.borderColor = "rgba(255, 170, 0, 0.3)";
    } else {
      $fpsCounter.style.color = "#ff4444";
      $fpsCounter.style.borderColor = "rgba(255, 68, 68, 0.3)";
    }
  }
}

function update() {
  now = Date.now();
  const offsetNow = now - raceStartTime;

  updateFPS();

  if (isInit) {
    processKeyPresses();
    if (!isPaused) {
      // Run physics at fixed 60 Hz timestep
      runPhysics(offsetNow, () => {
        updateCarsPhysics(offsetNow);
        if (showSparkEffects) updateSparksPhysics(offsetNow);
      });

      // Update ghost cars every render frame (time-based, not physics-based)
      updateGhosts(offsetNow);

      // Render with interpolation for smooth visuals
      const alpha = getInterpolationAlpha();
      renderCars(alpha);
      if (showSparkEffects) renderSparks(offsetNow, alpha);

      // Time-based updates (ghost recording, visuals, UI)
      recordGhostIfNeeded(offsetNow);
      updateCarsVisuals();
      updateViz(now);
      updateCamera(alpha);
    }
  }
  window.requestAnimationFrame(update);
}

initManifest();
